import {
  BaseClientEntities,
  Category,
  CategoryBase,
  Condition,
  PanelDesign,
  SectionBase,
  PanelDesignMode,
} from '@wix/add-panel-common';
import type { HttpError, HttpResponse } from '@wix/http-client';
import { EventEmitter } from 'fbemitter';
import { evaluateCondition } from '../AddPanel/utils/evaluateCondition';
import { isEntityValidOnConditions } from '../utils/entities/common';
import { transformEntitiesToPanelDesigns } from '../utils/entities/panelDesigns';
import { transformEntitiesToCategoriesList } from '../utils/entities/entities';
import type { AddPanelAPIOptions, PanelDesignData, PanelDesignDataSync } from './AddPanelAPI.types';
import { EventName } from './EventName';

interface AddPanelData {
  categories: Category[] | null;
  error: HttpError | null;
}

interface AddPanelDataSync extends AddPanelData {
  isLoading: boolean;
}

interface GetCategoryAndSection {
  category: CategoryBase;
  section?: SectionBase;
}

export class AddPanelAPI extends EventEmitter {
  private _rawEntities: BaseClientEntities | null = null;
  private _error: HttpError | null = null;
  private _isLoading = false;
  private _isLoaded = false;

  public version = '';
  public _response: HttpResponse | null = null;

  private readonly conditionHelpers: AddPanelAPIOptions['conditionHelpers'];
  private readonly hasSectionStyle: AddPanelAPIOptions['hasSectionStyle'];
  private readonly experiments: Required<AddPanelAPIOptions>['experiments'];
  private readonly fetcher: AddPanelAPIOptions['fetcher'];

  constructor(options: AddPanelAPIOptions) {
    super();

    const { conditionHelpers, hasSectionStyle, experiments, fetcher } = options;

    this.fetcher = fetcher;
    this.conditionHelpers = conditionHelpers;
    this.hasSectionStyle = hasSectionStyle;
    this.experiments = experiments || {};
  }

  get isLoaded() {
    return this._isLoaded;
  }

  private startLoading() {
    this._isLoading = true;
    this._isLoaded = false;
    this._error = null;
  }

  private endLoading() {
    this._isLoading = false;
    this._isLoaded = true;

    this.emit(EventName.DataLoad);
  }

  getConditionHelpers() {
    return this.conditionHelpers;
  }

  get error(): HttpError | null {
    return this._error;
  }

  hasError(): boolean {
    return Boolean(this._error);
  }

  hasData(): boolean {
    return Boolean(this._rawEntities);
  }

  async loadEntities(): Promise<void> {
    this.startLoading();

    try {
      const { version, entities } = await this.fetcher.loadEntities({ experiments: this.experiments });

      this.version = version;
      this._rawEntities = entities;
    } catch (error) {
      this._error = error as HttpError<any>;
    }

    this.endLoading();
  }

  private async settledData() {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.once(EventName.DataLoad, resolve);
    });
  }

  private evaluateConditionForEntity = (condition: Condition): boolean =>
    evaluateCondition(condition, this.conditionHelpers, 'add-panel');

  async getData(): Promise<AddPanelData> {
    await this.settledData();

    const { categories, error } = this.getDataSync();

    return { categories, error };
  }

  getDataSync(): AddPanelDataSync {
    try {
      const categories =
        this._rawEntities &&
        transformEntitiesToCategoriesList({
          entities: this._rawEntities,
          evaluateCondition: this.evaluateConditionForEntity,
          conditionHelpers: this.conditionHelpers,
          hasSectionStyle: this.hasSectionStyle,
        });

      return {
        isLoading: this._isLoading,
        error: this._error,
        categories,
      };
    } catch (error) {
      return {
        isLoading: this._isLoading,
        error: error as HttpError<any>,
        categories: [],
      };
    }
  }

  async getPanelDesignData(designMode = PanelDesignMode.Standalone): Promise<PanelDesignData> {
    await this.settledData();

    const { panelDesigns, error } = this.getPanelDesignDataSync(designMode);

    return { panelDesigns, error };
  }

  getPanelDesignDataSync(designMode = PanelDesignMode.Standalone): PanelDesignDataSync {
    try {
      if (!this._rawEntities) {
        return {
          isLoading: this._isLoading,
          error: this._error,
          panelDesigns: null,
        };
      }

      const entities = transformEntitiesToPanelDesigns({
        entities: this._rawEntities,
        evaluateCondition: (condition) => evaluateCondition(condition, this.conditionHelpers, 'design-panel'),
        hasSectionStyle: this.hasSectionStyle,
      });

      const panelDesigns: Record<string, PanelDesign> = Object.fromEntries(
        entities.panelDesigns.map((pd) => [pd.componentType, pd]),
      );

      return {
        isLoading: this._isLoading,
        error: this._error,
        panelDesigns,
      };
    } catch (error) {
      return {
        isLoading: this._isLoading,
        error: error as HttpError<any>,
        panelDesigns: {},
      };
    }
  }

  async getCategoryAndSection(categoryId: string, sectionName?: string) {
    await this.settledData();

    return this.getCategoryAndSectionSync(categoryId, sectionName);
  }

  getCategoryAndSectionSync(
    categoryId: string,
    necessarySectionName?: string,
    enhancedCategories?: Category[],
  ): GetCategoryAndSection | undefined {
    const isEntityValid = isEntityValidOnConditions(this.evaluateConditionForEntity);

    const categories = enhancedCategories || this._rawEntities?.categories;

    const category = categories?.find(
      (_category) => _category.id.toLowerCase() === categoryId.toLowerCase() && isEntityValid(_category),
    );

    if (!category) {
      return;
    }

    if (!necessarySectionName) {
      return { category };
    }

    const sections = enhancedCategories
      ? ((category as Category).groups.reduce(
          (acc: SectionBase[], { sections: _sections }) => [...acc, ..._sections],
          [],
        ) as SectionBase[])
      : this._rawEntities?.sections;

    const sectionsByName = sections?.filter(
      ({ sectionName }) => sectionName && sectionName.toLowerCase() === necessarySectionName.toLowerCase(),
    );

    if (!sectionsByName || sectionsByName.length === 0) {
      return { category };
    }

    const result: GetCategoryAndSection = { category };

    const section = sectionsByName.find(isEntityValid);

    if (section) {
      result.section = section;
    }

    return result;
  }
}
